import React, { useState, useMemo } from 'react';
import { Layout, Typography, Card, Row, Col, Tabs, Button, Pagination, Select, Input } from 'antd';
import { FaUserTie } from 'react-icons/fa';
import { SearchOutlined } from '@ant-design/icons';

const { Content } = Layout;
const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const mentors = [
  { id: 1, name: "Dr. Alice Mwangi", expertise: "Artificial Intelligence", bio: "Expert in Machine Learning and AI ethics.", location: "Nairobi, Kenya" },
  { id: 2, name: "John Okech", expertise: "Cybersecurity", bio: "Specialist in ethical hacking and network security.", location: "Lagos, Nigeria" },
  { id: 3, name: "Sophia Kalu", expertise: "Data Science", bio: "Data analytics and business intelligence professional.", location: "Cape Town, South Africa" },
  { id: 4, name: "David Kimani", expertise: "Artificial Intelligence", bio: "AI researcher focusing on deep learning.", location: "Accra, Ghana" },
  { id: 5, name: "Fatima Hassan", expertise: "Cybersecurity", bio: "Security analyst with 10+ years of experience.", location: "Cairo, Egypt" },
  { id: 6, name: "Elijah Otieno", expertise: "Data Science", bio: "Big data engineer working on AI-driven analytics.", location: "Kampala, Uganda" },
  { id: 7, name: "Eliud Mburu", expertise: "Artificial Intelligence", bio: "Researcher specializing in AI algorithms for healthcare.", location: "Nairobi, Kenya" },
  { id: 8, name: "Oluwaseun Adebayo", expertise: "Cybersecurity", bio: "Penetration tester with experience in threat analysis.", location: "Abuja, Nigeria" },
  { id: 9, name: "Grace Njeri", expertise: "Data Science", bio: "Data scientist working in predictive analytics and AI.", location: "Johannesburg, South Africa" },
  { id: 10, name: "Kwame Nkrumah", expertise: "Data Science", bio: "Data engineer specializing in machine learning pipelines.", location: "Accra, Ghana" },
  { id: 11, name: "Ruth Owusu", expertise: "Artificial Intelligence", bio: "AI practitioner focused on natural language processing.", location: "Lagos, Nigeria" },
  { id: 12, name: "Samuel Nyang'au", expertise: "Cybersecurity", bio: "Ethical hacker helping companies stay safe from cyber attacks.", location: "Mombasa, Kenya" },
];

const pageSize = 8;

const Mentorship = () => {
  const [currentTab, setCurrentTab] = useState("Artificial Intelligence");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const handleTabChange = (key) => {
    setCurrentTab(key);
    setCurrentPage(1);
    setSelectedCountry(null);
    setSearchQuery('');
  };

  const handleCountryChange = (value) => {
    setSelectedCountry(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const filteredMentors = useMemo(() => {
    let filtered = mentors.filter((mentor) => mentor.expertise === currentTab);
    if (selectedCountry) {
      filtered = filtered.filter((mentor) => mentor.location.includes(selectedCountry));
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (mentor) =>
          mentor.name.toLowerCase().includes(query) ||
          mentor.bio.toLowerCase().includes(query)
      );
    }
    return filtered;
  }, [currentTab, selectedCountry, searchQuery]);

  const paginatedMentors = useMemo(() => {
    return filteredMentors.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }, [filteredMentors, currentPage]);

  const uniqueCountries = [...new Set(mentors.map((mentor) => mentor.location.split(', ')[1]))];

  return (
    <Layout>
      <Content style={{ padding: '50px', background: 'linear-gradient(135deg, #f0f2f5, #e6fffb)' }}>
        <Title level={2} style={{ fontSize: '20px', display: 'flex', alignItems: 'center', color: '#13c2c2', marginBottom: '24px' }}>
          <FaUserTie style={{ marginRight: '12px', fontSize: '20px' }} /> Mentorship Program
        </Title>
        <Paragraph style={{ fontSize: '16px', color: '#595959', marginBottom: '24px' }}>
          Connect with industry experts in AI, Cybersecurity, and Data Science. Get guidance, career advice, and insights from professionals.
        </Paragraph>
        <div style={{ marginBottom: '24px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <Input
            placeholder="Search mentors by name or bio"
            prefix={<SearchOutlined />}
            style={{ width: '320px', borderRadius: '6px', height: '40px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
            onChange={handleSearchChange}
          />
          <Select
            showSearch
            placeholder="Filter by Country"
            style={{ width: '220px', borderRadius: '6px' }}
            onChange={handleCountryChange}
            allowClear
            dropdownStyle={{ borderRadius: '6px' }}
          >
            <Option value={null}>All Countries</Option>
            {uniqueCountries.map((country) => (
              <Option key={country} value={country}>
                {country}
              </Option>
            ))}
          </Select>
        </div>
        <Tabs
          defaultActiveKey="Artificial Intelligence"
          onChange={handleTabChange}
          className="custom-tabs"
          tabBarStyle={{ fontWeight: '600', color: '#13c2c2', borderBottom: '2px solid #13c2c2' }}
        >
          <TabPane tab="Artificial Intelligence" key="Artificial Intelligence" />
          <TabPane tab="Cybersecurity" key="Cybersecurity" />
          <TabPane tab="Data Science" key="Data Science" />
        </Tabs>
        <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
          {paginatedMentors.length > 0 ? (
            paginatedMentors.map((mentor) => (
              <Col xs={24} sm={12} md={8} lg={6} key={mentor.id}>
                <Card
                  hoverable
                  style={{
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
                    background: 'linear-gradient(135deg, #ffffff, #f0fafa)',
                    height: '300px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: '16px',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.15)';
                  }}
                >
                  <div>
                    <Title level={4} style={{ marginBottom: '12px', color: '#1a1a1a', fontSize: '18px', fontWeight: '600' }}>
                      {mentor.name}
                    </Title>
                    <Paragraph
                      style={{ color: '#595959', fontSize: '14px', marginBottom: '12px', lineHeight: '1.5' }}
                      ellipsis={{ rows: 3, expandable: false }}
                    >
                      {mentor.bio}
                    </Paragraph>
                    <Paragraph style={{ marginBottom: '8px', color: '#595959', fontSize: '14px' }}>
                      <strong style={{ color: '#13c2c2' }}>Expertise:</strong> {mentor.expertise}
                    </Paragraph>
                    <Paragraph style={{ marginBottom: '0', color: '#595959', fontSize: '14px' }}>
                      <strong style={{ color: '#13c2c2' }}>Location:</strong> {mentor.location}
                    </Paragraph>
                  </div>
                  <Button
                    type="primary"
                    icon={<FaUserTie style={{ marginRight: '8px' }} />}
                    style={{
                      background: 'linear-gradient(45deg, #13c2c2, #08979c)',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: 'bold',
                      height: '40px',
                      width: '100%',
                      marginTop: '16px',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => (e.target.style.background = 'linear-gradient(45deg, #08979c, #006d75)')}
                    onMouseLeave={(e) => (e.target.style.background = 'linear-gradient(45deg, #13c2c2, #08979c)')}
                  >
                    Connect
                  </Button>
                </Card>
              </Col>
            ))
          ) : (
            <Col span={24} style={{ textAlign: 'center', marginTop: '24px' }}>
              <Paragraph style={{ color: '#999', fontSize: '16px' }}>
                No mentors available for the selected criteria. Try adjusting your search or country filter!
              </Paragraph>
            </Col>
          )}
        </Row>
        {filteredMentors.length > pageSize && (
          <Pagination
            current={currentPage}
            total={filteredMentors.length}
            pageSize={pageSize}
            onChange={setCurrentPage}
            style={{ marginTop: '32px', textAlign: 'center', fontSize: '16px' }}
            showSizeChanger={false}
          />
        )}
        {/* Custom CSS */}
        <style jsx global>{`
          .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
            color: #13c2c2 !important;
          }
          .ant-tabs-ink-bar {
            background: #13c2c2 !important;
          }
          .ant-pagination-item-active {
            border-color: #13c2c2 !important;
          }
          .ant-pagination-item-active a {
            color: #13c2c2 !important;
          }
        `}</style>
      </Content>
    </Layout>
  );
};

export default Mentorship;